/**
 * Town NPCs (+field): FieldNpcDef figures (KayKit adventurers, animated idle, turning toward the
 * hero when they come close) or boards (bounty board, dungeon board as props), each with a
 * floating name + <title> and a quest marker ('!' available, '?' turn-in, grey '?' in progress)
 * driven by Renderer.setNpcMarkers. Picking (Renderer.npcAt) raycasts their hit boxes.
 * Not sim units: never targetable, never in RenderFrame.units.
 */
import * as THREE from 'three';
import type { FieldNpcDef, Vec2 } from '@keystone/sim';
import type { ToonAssets } from '../assets';
import { type HullRegistry, addHull, addOutlineNormals, ownedToonMaterial, stylize } from '../style';
import type { NpcMarker } from '../../types';
import { glowCards } from './glow';
import { disposeLabel, makeLabel, makeMarkerTexture } from './labels';
import { yawToward } from './layout';
import { ADVENTURER_GEAR as GEAR_NODES } from '../npcModels';
import { outdoorProc } from './procs';
import { QUEST_NPC_LOOKS } from './questNpcLooks'; // +quests: side-quest NPC looks and props

interface FigureSpec {
  file: 'Knight' | 'Mage' | 'Barbarian' | 'Rogue_Hooded';
  gear: string[];
  tint: number;
  scale: number;
  /** A prop next to the figure (proc id, local offset x / z). */
  prop?: { id: string; lx: number; lz: number; yaw?: number };
  /** +quests: idle clip (default 'Idle') and a chest glow. */
  clip?: string;
  glow?: readonly [number, number, number];
}

const ROLE_FIGURE: Partial<Record<FieldNpcDef['role'], FigureSpec>> = {
  questgiver: { file: 'Mage', gear: ['2H_Staff'], tint: 0xe8e0ff, scale: 1.0 },
  forge: { file: 'Barbarian', gear: ['1H_Axe'], tint: 0xffe6d0, scale: 1.08, prop: { id: 'anvil', lx: 1.9, lz: 0.6, yaw: -0.5 } },
  gear: { file: 'Knight', gear: ['Badge_Shield'], tint: 0xfff2dc, scale: 1.0, prop: { id: 'crates', lx: -1.6, lz: -0.4 } },
  vendor: { file: 'Rogue_Hooded', gear: [], tint: 0xfff0e0, scale: 1.0, prop: { id: 'barrel', lx: 1.3, lz: -0.3 } },
  guard: { file: 'Knight', gear: ['1H_Sword', 'Round_Shield'], tint: 0xdfe6ff, scale: 1.05 },
  villager: { file: 'Rogue_Hooded', gear: [], tint: 0xf4ffe8, scale: 0.95 },
};

const BOARD_ROLES: Partial<Record<FieldNpcDef['role'], string>> = { bounty_board: 'notice_board', dungeon_board: 'dungeon_board', lore: 'lore_stone', waystone: 'waystone' }; // +zone: lore stones, waystones

interface NpcView {
  def: FieldNpcDef;
  root: THREE.Group;
  body: THREE.Object3D | null;
  mixer: THREE.AnimationMixer | null;
  hitbox: THREE.Mesh;
  label: THREE.Sprite;
  marker: THREE.Sprite;
  markerKind: NpcMarker | null;
  height: number;
  yaw: number;
  baseYaw: number;
  phase: number;
  /** +quests: ground height and the bark hop's time left (s). */
  gy: number;
  hop: number;
}

const KAYKIT_H = 2.2;
/** +quests: length of the bark hop (s). */
const NPC_HOP_S = 0.5;
/** +quests: quest markers show within this camera distance (they draw over terrain, at a fixed screen size).
 *  Kept short so markers appear as you approach, not across the map (user feedback 2026-10-16). */
const NPC_MARKER_YD = 45;

export class TownNpcs {
  readonly group = new THREE.Group();
  private readonly views: NpcView[] = [];
  private readonly owned: { dispose(): void }[] = [];
  private readonly markerMats: Record<NpcMarker, THREE.SpriteMaterial>;
  private readonly hitGeo = new THREE.CylinderGeometry(1, 1, 1, 8).translate(0, 0.5, 0);
  private readonly hitMat = new THREE.MeshBasicMaterial({ visible: false });
  private readonly pickList: THREE.Object3D[] = [];
  private t = 0;

  constructor(defs: readonly FieldNpcDef[], assets: ToonAssets, private readonly hulls: HullRegistry, groundAt: (x: number, y: number) => number, shadows: boolean) {
    this.group.name = 'town-npcs';
    const tex = { available: makeMarkerTexture('!', false), turnin: makeMarkerTexture('?', false), progress: makeMarkerTexture('?', true), locked: makeMarkerTexture('!', true) }; // +quests: locked
    const mk = (t: THREE.Texture): THREE.SpriteMaterial => new THREE.SpriteMaterial({ map: t, transparent: true, depthTest: false, depthWrite: false, sizeAttenuation: false });
    this.markerMats = { available: mk(tex.available), turnin: mk(tex.turnin), progress: mk(tex.progress), locked: mk(tex.locked) };
    this.owned.push(tex.available, tex.turnin, tex.progress, tex.locked, ...Object.values(this.markerMats), this.hitGeo, this.hitMat);
    const procCache = new Map<string, THREE.BufferGeometry | null>();
    this.owned.push({ dispose: () => procCache.forEach((g) => g?.dispose()) });
    const propMat = ownedToonMaterial({ color: 0xffffff, vertexColors: true });
    this.owned.push(propMat);
    const glows: { x: number; y: number; z: number; size: number; color: readonly [number, number, number]; pulse?: number }[] = [];
    const prop = (id: string): THREE.Mesh | null => {
      const g = outdoorProc(id, procCache);
      if (!g) return null;
      if (!g.attributes['outlineNormal']) addOutlineNormals(g);
      const m = new THREE.Mesh(g, propMat);
      m.castShadow = shadows;
      m.receiveShadow = true;
      m.userData['noStyle'] = true;
      return m;
    };

    for (const def of defs) {
      const root = new THREE.Group();
      const gy = groundAt(def.pos.x, def.pos.y);
      root.position.set(def.pos.x, gy, -def.pos.y);
      const yaw = yawToward(Math.cos(def.facing), Math.sin(def.facing));
      root.rotation.y = yaw;
      this.group.add(root);
      const hint = (def.model ?? '').toLowerCase();
      const boardId = BOARD_ROLES[def.role] ?? (/board/.test(hint) ? (/dungeon/.test(hint + def.role) ? 'dungeon_board' : 'notice_board') : null);
      let body: THREE.Object3D | null = null;
      let mixer: THREE.AnimationMixer | null = null;
      let height = 2.4;
      if (boardId) {
        const m = prop(boardId);
        if (m) {
          root.add(m);
          addHull(m, propMat, hulls, 'prop');
        }
        height = boardId === 'dungeon_board' ? 3.2 : 3.0;
        if (boardId === 'waystone') {
          height = 5.4;
          glows.push({ x: root.position.x, y: root.position.y + 4.95, z: root.position.z, size: 3.2, color: [0.25, 0.62, 0.75], pulse: 0.45 });
        }
        if (boardId === 'dungeon_board') {
          const p = new THREE.Vector3(0, 1.85, 0.4).applyEuler(root.rotation).add(root.position);
          glows.push({ x: p.x, y: p.y, z: p.z, size: 2.2, color: [0.2, 0.7, 0.68], pulse: 0.4 });
        }
      } else {
        const spec = figureFor(def, hint);
        const model = assets.clone(spec.file) ?? assets.clone('Knight');
        if (model) {
          for (const n of GEAR_NODES) {
            const o = model.getObjectByName(n);
            if (o) o.visible = spec.gear.includes(n);
          }
          stylize(model, hulls, { tint: new THREE.Color(spec.tint), outline: true, castShadow: shadows, outlineRole: 'unit' });
          model.scale.setScalar(spec.scale);
          root.add(model);
          body = model;
          const clips = assets.clips(spec.file);
          const idle = THREE.AnimationClip.findByName(clips, spec.clip ?? 'Idle') ?? THREE.AnimationClip.findByName(clips, 'Idle') ?? clips[0]; // +quests: spec.clip
          if (idle) {
            mixer = new THREE.AnimationMixer(model);
            const a = mixer.clipAction(idle);
            a.play();
            a.time = Math.random() * idle.duration;
          }
          height = KAYKIT_H * spec.scale;
          if (spec.glow) glows.push({ x: root.position.x, y: root.position.y + height * 0.6, z: root.position.z, size: 3.4, color: spec.glow, pulse: 0.35 }); // +quests
        }
        if (spec.prop) {
          const m = prop(spec.prop.id);
          if (m) {
            m.position.set(spec.prop.lx, 0, spec.prop.lz);
            m.rotation.y = spec.prop.yaw ?? 0;
            root.add(m);
            addHull(m, propMat, hulls, 'prop');
            if (spec.prop.id === 'anvil') {
              const p = new THREE.Vector3(spec.prop.lx - 1.7, 1.05, spec.prop.lz - 0.2).applyEuler(root.rotation).add(root.position);
              glows.push({ x: p.x, y: p.y, z: p.z, size: 2.4, color: [0.6, 0.3, 0.08], pulse: 0.3 });
            }
          }
        }
      }
      const hitbox = new THREE.Mesh(this.hitGeo, this.hitMat);
      hitbox.scale.set(boardId ? 1.5 : 0.8, height + 0.3, boardId ? 1.5 : 0.8);
      hitbox.userData['npcId'] = def.id;
      root.add(hitbox);
      const label = makeLabel(def.name, { ...(def.title ? { title: def.title } : {}), color: '#9ff08c', size: 26 });
      label.position.set(def.pos.x, gy + height + 0.45, -def.pos.y);
      this.group.add(label);
      const marker = new THREE.Sprite(this.markerMats.available);
      marker.center.set(0.5, 0);
      marker.scale.set(0.05, 0.066, 1);
      marker.renderOrder = 21;
      marker.visible = false;
      marker.position.set(def.pos.x, gy + height + 1.45, -def.pos.y);
      this.group.add(marker);
      this.views.push({ def, root, body, mixer, hitbox, label, marker, markerKind: null, height, yaw, baseYaw: yaw, phase: Math.random() * 6, gy, hop: 0 });
    }
    const gm = glowCards(glows);
    if (gm) {
      this.owned.push(gm.geometry, gm.material as THREE.Material);
      this.group.add(gm);
    }
  }

  /** +quests: a little hop (a bark, a quest handed in). */
  emote(npcId: string): void {
    const v = this.views.find((x) => x.def.id === npcId);
    if (v?.body) v.hop = NPC_HOP_S;
  }

  setMarkers(m: Record<string, NpcMarker | null>): void {
    for (const v of this.views) {
      const k = m[v.def.id] ?? null;
      v.markerKind = k;
      v.marker.visible = !!k;
      if (k) v.marker.material = this.markerMats[k];
    }
  }

  /** Per frame: idle animation (on-screen only), turn toward a nearby hero, marker bob. */
  update(dtS: number, camera: THREE.Camera, frustum: THREE.Frustum, hero: Vec2 | null): void {
    this.t += dtS;
    const sphere = new THREE.Sphere();
    for (const v of this.views) {
      sphere.center.copy(v.root.position);
      sphere.center.y += v.height / 2;
      sphere.radius = v.height + 1;
      const on = frustum.intersectsSphere(sphere);
      if (on && v.mixer) v.mixer.update(dtS);
      // Figures turn to face a hero within 7 yd, then drift back to their post.
      // +quests: a bark hop (a little jump, then settle).
      if (v.hop > 0) {
        v.hop = Math.max(0, v.hop - dtS);
        const k = 1 - v.hop / NPC_HOP_S;
        v.root.position.y = v.gy + Math.sin(k * Math.PI) * 0.32 * (v.hop > 0 ? 1 : 0);
      }
      if (v.body) {
        let want = v.baseYaw;
        if (hero) {
          const dx = hero.x - v.def.pos.x;
          const dy = hero.y - v.def.pos.y;
          if (dx * dx + dy * dy < 49 && dx * dx + dy * dy > 0.5) want = yawToward(dx, dy);
        }
        let d = want - v.yaw;
        d = Math.atan2(Math.sin(d), Math.cos(d));
        v.yaw += d * Math.min(1, dtS * 4);
        v.root.rotation.y = v.yaw;
      }
      const cd = camera.position.distanceTo(v.root.position);
      v.label.visible = cd < 70;
      v.marker.visible = !!v.markerKind && cd < NPC_MARKER_YD; // +quests: no far-off markers through the hills
      if (v.marker.visible) {
        const bob = Math.sin(this.t * 2.4 + v.phase) * 0.12;
        v.marker.position.y = v.root.position.y + v.height + 1.45 + bob;
        const s = 1 + 0.06 * Math.sin(this.t * 4.8 + v.phase);
        v.marker.scale.set(0.05 * s, 0.066 * s, 1);
      }
    }
  }

  /** The NPC whose hit box the ray crosses first, or null. */
  pick(ray: THREE.Raycaster): string | null {
    this.pickList.length = 0;
    for (const v of this.views) this.pickList.push(v.hitbox);
    const hits = ray.intersectObjects(this.pickList, false);
    return (hits[0]?.object.userData['npcId'] as string | undefined) ?? null;
  }

  /** Every NPC hit box (prewarm / debugging). */
  get count(): number {
    return this.views.length;
  }

  dispose(): void {
    for (const v of this.views) {
      v.mixer?.stopAllAction();
      if (v.body) v.mixer?.uncacheRoot(v.body);
      disposeLabel(v.label);
      v.marker.removeFromParent();
    }
    this.group.traverse((o) => {
      if (o.userData['hull']) {
        this.hulls.unit.delete(o);
        this.hulls.prop.delete(o);
      }
    });
    for (const o of this.owned) o.dispose();
    this.group.removeFromParent();
  }
}

function figureFor(def: FieldNpcDef, hint: string): FigureSpec {
  const quest = QUEST_NPC_LOOKS[def.id]; // +quests
  if (quest) return quest;
  const base = ROLE_FIGURE[def.role] ?? ROLE_FIGURE.villager!;
  if (/knight|guard/.test(hint)) return { ...base, file: 'Knight', gear: base.file === 'Knight' ? base.gear : ['1H_Sword', 'Round_Shield'] };
  if (/mage|elder|wizard/.test(hint)) return { ...base, file: 'Mage', gear: ['2H_Staff'] };
  if (/barbarian|smith/.test(hint)) return { ...base, file: 'Barbarian', gear: ['1H_Axe'] };
  if (/rogue|hood|merchant/.test(hint)) return { ...base, file: 'Rogue_Hooded', gear: [] };
  if (/anvil/.test(hint) && def.role !== 'forge') return { ...base, prop: { id: 'anvil', lx: 1.9, lz: 0.6, yaw: -0.5 } };
  return base;
}
